import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { useProBuilderApp, useProBuilderStatus } from '~/hooks/queries/use-pro-builder';
import { personFacingMessage } from '~/lib/query-client';
import { systemSettingsPath } from '~/lib/system-settings-sections';
import {
  PRO_BUILDER_ORG_OFF_ADMIN_LEAD,
  PRO_BUILDER_ORG_OFF_ADMIN_LINK,
  PRO_BUILDER_ORG_OFF_MEMBER,
} from './pro-builder-copy';
import { ProBuilderAppPicker, ProBuilderNewApp } from './pro-builder-app-picker';
import { useProBuilderPickerStore } from './pro-builder-picker-store';
import { useProBuilderSessionStore } from './pro-builder-session-store';
import { ProBuilderTerminal } from './pro-builder-terminal';
import { ProBuilderHeaderBar } from './pro-builder-window';

export const APP_LOAD_ERROR = "Couldn't load your app. Try again in a moment.";
/** The binding read lists the folder through the sandbox, which can take up to ~90 s on a cold workspace. */
export const WORKSPACE_STARTING = 'Starting your workspace…';

/**
 * The Pro Builder window body (FRONT-3003): the status read's four states, each
 * under the window's one header bar (so the close button is always there).
 * Loading keys off the query's own pending flag and an error has its own
 * branch (CLAUDE.md FRONT-1525 — a failed query is neither pending nor has
 * data). That branch is taken only when there is NO data: a failed REFETCH
 * (after any invalidation, e.g. saving a connection) keeps the last answer,
 * and swapping the body for an error would unmount the terminal and detach it
 * from a running Claude Code, which then has to be re-attached. The terminal mounts ONLY when the organization switch is on,
 * so no session is opened for a person who cannot use one.
 *
 * The connection, billing sentence and credential restart offer live in
 * Settings → Pro Builder, reading the same session store
 * (`pro-builder-session-store.ts`); the header gear has its own restart for the
 * auto-compact threshold (FRONT-3228). Saving never restarts the terminal — only
 * the person's click on one of those restart buttons does.
 * What the running shell was started with is recorded by the terminal itself,
 * after each open, from the open response (`pro-builder-terminal.tsx`) — not
 * here from whatever answer this read happens to hold.
 */
export function ProBuilderBody({ onClose }: { onClose: () => void }) {
  const status = useProBuilderStatus();
  const data = status.data;

  if (status.isError && !data) {
    return (
      <StateBody onClose={onClose}>
        <p role="alert" data-testid="pro-builder-error" className="text-sm text-destructive">
          Couldn't load Pro Builder. Try again in a moment.
        </p>
      </StateBody>
    );
  }
  // After the branch above, a missing `data` can only mean the first read is pending.
  if (status.isPending || !data) {
    return (
      <StateBody onClose={onClose}>
        <Skeleton data-testid="pro-builder-loading" className="h-48 w-full rounded-lg" />
      </StateBody>
    );
  }

  const { orgEnabled, canManageOrg } = data;

  if (!orgEnabled) {
    return (
      <StateBody onClose={onClose}>
        <div data-testid="pro-builder-org-off" className="text-sm text-muted-foreground">
          {canManageOrg ? (
            <>
              {PRO_BUILDER_ORG_OFF_ADMIN_LEAD}{' '}
              <Link to={systemSettingsPath('sandbox')} className="font-medium text-foreground underline underline-offset-2">
                {PRO_BUILDER_ORG_OFF_ADMIN_LINK}
              </Link>
            </>
          ) : (
            PRO_BUILDER_ORG_OFF_MEMBER
          )}
        </div>
      </StateBody>
    );
  }

  return <BoundBody onClose={onClose} />;
}

/**
 * FRONT-3077: the window is bound to ONE app. No binding shows the picker in
 * place of the terminal; a binding shows the terminal, or — after the header's
 * New — the "New app" view under the same header (switcher still there, New
 * hidden, as the guided Builder does while creating). Moving to another app is
 * the header's switcher. Mounted only after the organization switch is known to be on, so
 * the binding is never read for a person who cannot use Pro Builder. Loading
 * keys off the pending flag, a failed first read has its own branch, and a
 * failed REFETCH keeps the last answer (the terminal must not unmount).
 */
function BoundBody({ onClose }: { onClose: () => void }) {
  const app = useProBuilderApp(true);
  const creating = useProBuilderPickerStore((s) => s.creating);
  const terminalKey = useProBuilderSessionStore((s) => s.session.terminalKey);
  const data = app.data;

  if (app.isError && !data) {
    return (
      <StateBody onClose={onClose}>
        <div className="flex flex-col items-start gap-2">
          <p role="alert" data-testid="pro-builder-error" className="text-sm text-destructive">
            {personFacingMessage(app.error, APP_LOAD_ERROR)}
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => void app.refetch()}>
            Try again
          </Button>
        </div>
      </StateBody>
    );
  }
  if (app.isPending || !data) {
    return (
      <StateBody onClose={onClose}>
        <div className="flex flex-col gap-3">
          <p data-testid="pro-builder-workspace-starting" className="text-sm text-muted-foreground">
            {WORKSPACE_STARTING}
          </p>
          <Skeleton data-testid="pro-builder-loading" className="h-48 w-full rounded-lg" />
        </div>
      </StateBody>
    );
  }
  if (data.folder === null) {
    return (
      <StateBody onClose={onClose}>
        <ProBuilderAppPicker />
      </StateBody>
    );
  }
  if (creating) {
    return (
      <>
        <ProBuilderHeaderBar onClose={onClose} view="new" />
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          <ProBuilderNewApp />
        </div>
      </>
    );
  }
  return <ProBuilderTerminal key={terminalKey} terminalKey={terminalKey} onClose={onClose} />;
}

/** Loading / error / org-off / the first-time picker: the window's header bar (crumb and × only) over a plain padded body. */
function StateBody({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  return (
    <>
      <ProBuilderHeaderBar onClose={onClose} />
      <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
    </>
  );
}
